import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-14465">
      <div className="mb-8" data-id="element-14466">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-14467">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-14468">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-14469">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-14470">
          <div className="p-5" data-id="element-14471">
            <div className="flex items-center" data-id="element-14472">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-14473">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-14474" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14475">
                <dl data-id="element-14476">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14477">
                    Total Budget
                  </dt>
                  <dd data-id="element-14478">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14479">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-14480">
          <div className="p-5" data-id="element-14481">
            <div className="flex items-center" data-id="element-14482">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-14483">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-14484" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14485">
                <dl data-id="element-14486">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14487">
                    Total Actual
                  </dt>
                  <dd data-id="element-14488">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14489">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-14490">
          <div className="p-5" data-id="element-14491">
            <div className="flex items-center" data-id="element-14492">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-14493">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-14494" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-14495" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-14496">
                <dl data-id="element-14497">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14498">
                    Variance
                  </dt>
                  <dd data-id="element-14499">
                    <div className="text-lg font-medium text-gray-900" data-id="element-14500">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-14501">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-14502">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-14503">
          <ResponsiveContainer width="100%" height="100%" data-id="element-14504">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-14505">
              <CartesianGrid strokeDasharray="3 3" data-id="element-14506" />
              <XAxis dataKey="department" data-id="element-14507" />
              <YAxis data-id="element-14508" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-14509" />
              <Legend data-id="element-14510" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-14511" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-14512" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-14513">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-14514">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-14515">
          <ResponsiveContainer width="100%" height="100%" data-id="element-14516">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-14517">
              <CartesianGrid strokeDasharray="3 3" data-id="element-14518" />
              <XAxis dataKey="month" data-id="element-14519" />
              <YAxis data-id="element-14520" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-14521" />
              <Legend data-id="element-14522" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-14523" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-14524" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14525">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-14526">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14527">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-14528">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-14529">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-14530">
            <thead className="bg-gray-50" data-id="element-14531">
              <tr data-id="element-14532">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14533">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14534">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14535">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14536">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14537">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-14538">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-14539">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14540">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-14541">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-14542">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-14543">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-14544">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-14545">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-14546">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}